import React from "react";
import { Container, Typography, Box, Stack, Divider, Alert, Chip, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGavel, faTree, faUsers, faBullhorn, faHandPaper, faCalendarDays, faLocationDot } from "@fortawesome/free-solid-svg-icons";

const POST20240606: React.FC = () => {
    return (
        <Container sx={{ mb: 5 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

            <Box mt={3}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    align="center"
                    gutterBottom
                    color="primary"
                    sx={{ letterSpacing: 1 }}
                >
                    PLUi&nbsp;: Quels choix pour Bois-le-Roi&nbsp;? Réunions en juin&nbsp;&nbsp;
                    <FontAwesomeIcon icon={faTree} color="#388e3c" style={{ verticalAlign: "middle" }}/>
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
                <Typography fontWeight={700}>
                    Tract rédigé avec l’association Bois-le-Roi Environnement, <br />
                    le soutien de la liste Réussir Ensemble à Bois-le-Roi et le collectif de l’Arbre.
                </Typography>
            </Alert>

            <Stack spacing={2}>
                <Typography variant="body1">
                    <b>Courant mai</b>, plusieurs associations préoccupées par l’environnement et le cadre de vie ont demandé à connaître les choix des <b>Nouvelles Règles d’Urbanisme pour le PLUi</b> (<i>P.L.U. intercommunal*</i>), qui impacteront les <b>26 communes de la CAPF</b>. Ces règles définiront l’aspect de chaque commune&nbsp;: hauteurs, marges de recul, présence de verdure…
                </Typography>
                <Divider textAlign="left" sx={{ my: 2 }}>
                    <Chip icon={<FontAwesomeIcon icon={faGavel} />} label="Décisions prises sans concertation" color="warning" />
                </Divider>

                <Typography variant="body1">
                    Pour Bois-le-Roi, <strong>les règles ont été décidées par le Maire seul</strong>, sans aucune concertation ni information sur sa stratégie d’urbanisme. Après la pression des associations, le Maire a d’abord promis une réunion publique en mai, puis, sans explication, quatre réunions de quartiers sont prévues en juin, découpant la commune arbitrairement.
                </Typography>

                <Box mt={2} mb={2}>
                    <Alert severity="warning" icon={<FontAwesomeIcon icon={faHandPaper} />}>
                        <b>ATTENTION</b> : Cette méthode divise les habitants pour imposer des choix déjà arrêtés…
                    </Alert>
                </Box>

                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    <FontAwesomeIcon icon={faUsers} color="#1976d2" />&nbsp;
                    Mai 2023 : mobilisation importante des Bacots écartée lors du vote sur le PLH.
                </Typography>
                <Typography variant="body1">
                    La parole citoyenne a été empêchée. Au final, la densification actée par la CAPF a été réduite sans réelle transparence via une « pirouette » en Conseil Municipal.
                </Typography>

                <Divider textAlign="right" sx={{ my: 2 }}>
                    <Chip icon={<FontAwesomeIcon icon={faBullhorn} />} label="Il faut AGIR !" color="error" />
                </Divider>

                <Typography
                    variant="h6"
                    fontWeight={700}
                    color="error"
                    align="center"
                    gutterBottom
                    sx={{ letterSpacing: 1 }}
                >
                    NE NOUS LAISSONS PAS ABUSER PAR CETTE MÉTHODE ANTI-DÉMOCRATIQUE
                </Typography>

                <Typography variant="body1">
                    Le Maire refuse toujours de communiquer les documents PLUi en préparation, promettant seulement un projet «&nbsp;finalisé&nbsp;». C’est empêcher tout débat citoyen éclairé. <br/>
                    <strong>Or, sans mobilisation, les décisions seront entérinées d'ici fin 2024</strong>, engageant l’avenir de Bois-le-Roi sur la base des seuls choix du Maire.<br/><br/>
                    <b>Déjà, lors des consultations précédentes, 500 avis s’y étaient opposés… sans aucun effet.</b>
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" color="secondary" align="center" gutterBottom>
                    📢 <b>Participons massivement&nbsp;!</b>
                </Typography>
                <Typography fontWeight={700} align="center">
                    Où que vous habitiez à Bois-le-Roi&nbsp;: <br/>
                    <span style={{ color: "#388e3c" }}><FontAwesomeIcon icon={faHandPaper} /> Venez nombreux aux <b>4 réunions publiques</b> pour défendre notre qualité de vie&nbsp;!</span>
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCalendarDays} style={{ color: "#1976d2" }} />
                        </ListItemIcon>
                        <ListItemText primary="Samedi 15 juin – 10h30 au préau Métra" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCalendarDays} style={{ color: "#1976d2" }} />
                        </ListItemIcon>
                        <ListItemText primary="Mardi 18 juin – 19h30 en mairie" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCalendarDays} style={{ color: "#1976d2" }} />
                        </ListItemIcon>
                        <ListItemText primary="Lundi 24 juin – 19h30 en mairie" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCalendarDays} style={{ color: "#1976d2" }} />
                        </ListItemIcon>
                        <ListItemText primary="Mercredi 26 juin – 19h30 en mairie" />
                    </ListItem>
                </List>
                <Typography align="center" color="primary" fontWeight={700} mt={2}>
                    Prenez la parole pour affirmer votre attachement à Bois-le-Roi !<br/>
                    <span style={{ fontSize: "1rem", fontWeight: 400 }}>
                        Pourquoi aimez-vous y vivre ? <i>Exprimez vos attentes pour le futur cadre de vie…</i>
                    </span>
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Alert
                    severity="success"
                    icon={<FontAwesomeIcon icon={faTree} />}
                    sx={{ fontWeight: 600, fontSize: "1.05rem" }}
                >
                    <b>Démocratie exemplaire à Chartrettes</b><br />
                    Nos voisins de Chartrettes ont été impliqués à chaque étape du PLUi, leurs élus partageant et ajustant les projets avec les habitants pour préserver cadre de vie et esprit « village ».
                </Alert>
            </Stack>
        </Container>
    );
};

export default POST20240606;